import { useState } from 'react';
import styles from './ReflectionPrompt.module.css';

// Three questions the telemetry cannot answer for itself: what the driver was
// trying to do, how the car behaved, and what happened that was not driving.
// Those are frequently what separates a fault from a deliberate choice.
const FIELDS = [
  { key: 'goal', label: 'What were you working on?',
    placeholder: 'e.g. trail braking into the hairpin' },
  { key: 'car_feel', label: 'How did the car feel?',
    placeholder: 'e.g. loose on entry, pushing at the exit' },
  { key: 'unusual', label: 'Anything unusual?',
    placeholder: 'e.g. traffic, a yellow flag, worn tyres' },
];

/**
 * Optional context asked before Deep Analysis.
 *
 * Skippable in one click, and skipping is the primary action rather than a way
 * out: a required form ahead of the analysis would be a toll gate on the
 * feature's main path, and most drivers will want the report, not a
 * questionnaire. Whatever is supplied reaches the model explicitly labelled as
 * the driver's own account, never as something measured.
 */
export default function ReflectionPrompt({ onSubmit, onSkip }) {
  const [values, setValues] = useState({});

  const update = (key, value) => setValues(prev => ({ ...prev, [key]: value }));
  const filled = Object.values(values).some(v => (v || '').trim());

  const submit = event => {
    event.preventDefault();
    const cleaned = Object.fromEntries(
      Object.entries(values)
        .map(([k, v]) => [k, (v || '').trim()])
        .filter(([, v]) => v),
    );
    onSubmit(Object.keys(cleaned).length ? cleaned : null);
  };

  return (
    <form className={styles.wrap} onSubmit={submit}>
      <div className={styles.intro}>
        <strong>Before we look at the data</strong>
        <span>
          Optional — but the telemetry can&rsquo;t see what you were trying to do,
          and that changes what&rsquo;s worth coaching.
        </span>
      </div>
      {FIELDS.map(field => (
        <label key={field.key} className={styles.field}>
          <span>{field.label}</span>
          <input
            type="text"
            value={values[field.key] || ''}
            placeholder={field.placeholder}
            maxLength={400}
            onChange={e => update(field.key, e.target.value)}
          />
        </label>
      ))}
      <div className={styles.actions}>
        <button type="button" className={styles.skip} onClick={onSkip}>
          Skip
        </button>
        <button type="submit" className={styles.go} disabled={!filled}>
          Analyse with this context
        </button>
      </div>
    </form>
  );
}
